import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';

const STORE_RESET_NEEDLES = ['store reset while query was in flight'] as const;
const ORGANIZATION_NOT_FOUND_NEEDLES = ['organization not found'] as const;
const INVALID_WEBSITE_NEEDLES = ['invalid website'] as const;
const SURVEY_EXPIRED_NEEDLES = [
  'survey has expired',
  'project is not published',
  'unpublished project',
] as const;
const DUPLICATE_DOCUMENT_URL_NEEDLES = [
  'document with this url already exists',
] as const;
const QUESTION_NOT_FOUND_NEEDLES = ['question not found'] as const;
const MIN_SELECTIONS_EXCEEDS_MAX_ERROR_CODES = [
  'QUESTIONS_MIN_SELECTIONS_EXCEEDS_MAX',
] as const;
const NOT_FOUND_NEEDLES = ['not found'] as const;
const PERMISSION_NEEDLES = [
  'permission',
  'forbidden',
  'not authorized',
] as const;
const UNAUTHORIZED_NEEDLES = ['unauthorized', 'unauthenticated'] as const;
const QUESTION_TYPE_CHANGE_KEYWORDS = [
  'question type',
  'change',
  'cannot',
  'type',
] as const;

function hasStringMessage(value: object): value is { message: string } {
  if (!('message' in value)) return false;
  const record: Record<string, unknown> = value;
  return typeof record['message'] === 'string';
}

function collectMessages(error: unknown): string[] {
  if (error == null) return [];
  const messages: string[] = [];
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((e) => {
      if (e.message) messages.push(e.message);
    });
    if (error.message) messages.push(error.message);
    return messages;
  }
  if (error instanceof Error) {
    if (error.message) messages.push(error.message);
    return messages;
  }
  if (typeof error === 'object' && hasStringMessage(error)) {
    messages.push(error.message);
  }
  return messages;
}

function matchesErrorMessage(
  error: unknown,
  needles: readonly string[]
): boolean {
  return collectMessages(error).some((message) => {
    const lower = message.toLowerCase();
    return needles.some((needle) => lower.includes(needle));
  });
}

interface ErrorExtensions {
  code?: string;
  errorCode?: string;
  status?: number;
}

function extractExtensions(extensions: unknown): ErrorExtensions {
  if (typeof extensions !== 'object' || extensions === null) return {};
  const code = Reflect.get(extensions, 'code');
  const errorCode = Reflect.get(extensions, 'errorCode');
  const status = Reflect.get(extensions, 'status');
  return {
    code: typeof code === 'string' ? code : undefined,
    errorCode: typeof errorCode === 'string' ? errorCode : undefined,
    status: typeof status === 'number' ? status : undefined,
  };
}

function collectExtensions(error: unknown): ErrorExtensions[] {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.map((graphQLError) =>
      extractExtensions(graphQLError.extensions)
    );
  }
  if (typeof error === 'object' && error !== null && 'extensions' in error) {
    return [extractExtensions(Reflect.get(error, 'extensions'))];
  }
  return [];
}

function matchesExtension(
  error: unknown,
  match: { codes?: readonly string[]; statuses?: readonly number[] }
): boolean {
  const { codes = [], statuses = [] } = match;
  return collectExtensions(error).some(
    ({ code, status }) =>
      (code !== undefined && codes.includes(code)) ||
      (status !== undefined && statuses.includes(status))
  );
}

export function isApolloStoreResetError(error: unknown): boolean {
  return matchesErrorMessage(error, STORE_RESET_NEEDLES);
}

// GraphQL-response failures are already reported per-operation by the Apollo
// errorLink; callers use this to skip duplicate captures while still
// reporting network-only ApolloErrors, which the errorLink never captures.
export function hasGraphQLErrors(error: unknown): boolean {
  return CombinedGraphQLErrors.is(error) && error.errors.length > 0;
}

export function isOrganizationNotFoundError(error: unknown): boolean {
  return (
    matchesErrorMessage(error, ORGANIZATION_NOT_FOUND_NEEDLES) ||
    matchesExtension(error, {
      codes: ['ORGANIZATION_NOT_FOUND', 'NOT_FOUND'],
      statuses: [404],
    })
  );
}

export function isInvalidWebsiteError(error: unknown): boolean {
  return matchesErrorMessage(error, INVALID_WEBSITE_NEEDLES);
}

export function isSurveyExpiredError(error: unknown): boolean {
  return matchesErrorMessage(error, SURVEY_EXPIRED_NEEDLES);
}

export function isDuplicateDocumentUrlError(error: unknown): boolean {
  return matchesErrorMessage(error, DUPLICATE_DOCUMENT_URL_NEEDLES);
}

export function isQuestionNotFoundError(error: unknown): boolean {
  return matchesErrorMessage(error, QUESTION_NOT_FOUND_NEEDLES);
}

export function isMinSelectionsExceedsMaxError(error: unknown): boolean {
  return collectExtensions(error).some(
    ({ errorCode }) =>
      errorCode !== undefined &&
      MIN_SELECTIONS_EXCEEDS_MAX_ERROR_CODES.some((code) => code === errorCode)
  );
}

export function isNotFoundError(error: unknown): boolean {
  return (
    matchesErrorMessage(error, NOT_FOUND_NEEDLES) ||
    matchesExtension(error, { codes: ['NOT_FOUND'], statuses: [404] })
  );
}

export function isPermissionError(error: unknown): boolean {
  return (
    matchesErrorMessage(error, PERMISSION_NEEDLES) ||
    matchesExtension(error, {
      codes: ['FORBIDDEN', 'UNAUTHORIZED'],
      statuses: [403],
    })
  );
}

export function isUnauthorizedError(error: unknown): boolean {
  return (
    matchesErrorMessage(error, UNAUTHORIZED_NEEDLES) ||
    matchesExtension(error, {
      codes: ['UNAUTHENTICATED', 'UNAUTHORIZED'],
      statuses: [401],
    })
  );
}

const SERVER_AUTH_STATUSES = new Set([401, 403]);

// A transport-level (whole-response) 401/403 the errorLink recovers by
// refreshing the token, distinct from a GraphQL-200 FORBIDDEN/UNAUTHENTICATED.
export function isServerAuthError(error: unknown): error is ServerError {
  return ServerError.is(error) && SERVER_AUTH_STATUSES.has(error.statusCode);
}

export function isQuestionTypeChangeConflictError(error: unknown): boolean {
  if (isQuestionNotFoundError(error)) return true;
  return collectMessages(error).some((message) => {
    const lower = message.toLowerCase();
    return (
      lower.includes('insights') &&
      QUESTION_TYPE_CHANGE_KEYWORDS.some((keyword) => lower.includes(keyword))
    );
  });
}
